import {
  ChakraFlex,
  ChakraText,
  FAIconName,
  FireflyButton,
  Icon,
  ModalV2,
  Text,
} from '@equip.health/ui';

import { SafetyRiskAlertTriggerReasonCode } from '~/lib/types/survey/SafetyRiskAlert';

const TOP_LEVEL_ID = 'safety-risk-alert-modal';

const BANNER_COPY_BY_TRIGGER: Partial<Record<string, string>> = {
  [SafetyRiskAlertTriggerReasonCode.PHQ9_Q9_WITH_NONZERO_DONE]:
    'Your patient has endorsed thoughts of suicidal ideation and/or self-harm on their PHQ-9. Please review the response, and conduct a thorough assessment using the C-SSRS SLC.',
  [SafetyRiskAlertTriggerReasonCode.PHQ9_Q9_WITH_NONZERO_IN_PROGRESS]:
    'Your patient has endorsed thoughts of suicidal ideation and/or self-harm on their PHQ-9 but has not yet submitted their PHQ-9. Please review the response and conduct a thorough assessment using the C-SSRS SLC in the very next session, or reach out by phone if the patient does not enter session.',
  [SafetyRiskAlertTriggerReasonCode.PHQ9_Q1_THRU_8_ANSWERED_WITH_NULL_Q9]:
    'Your patient has skipped the suicidal ideation/self-harm question on their PHQ-9. Please review the response and conduct a thorough assessment using the C-SSRS SLC in the very next session, or reach out by phone if the patient does not enter session.',
};

const DEFAULT_BANNER_COPY =
  BANNER_COPY_BY_TRIGGER[
    SafetyRiskAlertTriggerReasonCode.PHQ9_Q9_WITH_NONZERO_DONE
  ]!;

export type SafetyRiskAlertModalProps = {
  errorMessage: string | null;
  isSubmitting: boolean;
  onAcknowledge: () => void;
  triggerReasonCode?: string;
};

const SafetyRiskAlertModal = ({
  errorMessage,
  isSubmitting,
  onAcknowledge,
  triggerReasonCode,
}: SafetyRiskAlertModalProps) => {
  const bannerCopy =
    (triggerReasonCode && BANNER_COPY_BY_TRIGGER[triggerReasonCode]) ??
    DEFAULT_BANNER_COPY;

  return (
  <ModalV2
    footerContent={
      <ChakraFlex
        alignItems="center"
        gap="8px"
        justifyContent="flex-end"
        width="100%"
      >
        {errorMessage && (
          <ChakraText
            color="critical.base"
            data-testid="safety-alert-error"
            flex="1"
            fontSize="13px"
          >
            {errorMessage}
          </ChakraText>
        )}
        <FireflyButton
          id={`${TOP_LEVEL_ID}__acknowledge`}
          isLoading={isSubmitting}
          leftIcon={FAIconName.checkRegular}
          onClick={onAcknowledge}
        >
          {errorMessage ? 'Retry' : 'Acknowledge'}
        </FireflyButton>
      </ChakraFlex>
    }
    hideCloseButton
    id={TOP_LEVEL_ID}
    isOpen
    onClose={() => {}}
    shouldCloseOnEscape={false}
    shouldCloseOnOverlayClick={false}
    size="xl"
    title="Review patient safety risk"
  >
    <ChakraFlex direction="column" gap="18px" width="100%">
      <ChakraFlex
        alignItems="center"
        background="critical.background"
        borderRadius="12px"
        data-testid="safety-alert-warning-banner"
        gap="8px"
        padding="12px 16px"
        width="100%"
      >
        <Icon
          color="critical.text"
          name={FAIconName.exclamationTriangleRegular}
        />
        <Text>{bannerCopy}</Text>
      </ChakraFlex>
      <Text>
        Acknowledging this message will open the patient&rsquo;s chart in a new
        tab.
      </Text>
    </ChakraFlex>
  </ModalV2>
  );
};

export default SafetyRiskAlertModal;
